import { Asset } from 'nftx.js';
import { useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import Icon, { Icons } from '@/components/elements/Icon';
import InfiniteScroll from '@/components/modules/InfiniteScroll';
import { Controller, useWatch } from 'react-hook-form';
import { Form } from './types';
import SelectInventoryFormItem from './SelectInventoryFormItem';
import { QueryStatus } from '@tanstack/react-query';
import FilterToggle from '@/presentation/components/modules/filters/FilterToggle';
import useTraitFilters from '@/presentation/hooks/filters/useTraitFilters';
import { FilterModal } from '@/presentation/components/modals/FilterModal';

export interface SelectInventoryProps {
  collectionName: string;
  userAssets: Asset[] | undefined;
  filterCount?: number;
  is1155: boolean;
  control: Form['control'];
  errors: Form['formState']['errors'];
  setValue: Form['setValue'];
  withinModal?: boolean;
  showCounter?: boolean;
  status: QueryStatus;
}

const PAGE_SIZE = 12;

export const SelectInventoryForm = ({
  collectionName,
  userAssets = [],
  filterCount,
  control,
  setValue,
  is1155,
  withinModal,
  showCounter,
  status,
}: SelectInventoryProps) => {
  const [page, setPage] = useState(1);
  const visibleLimit = page * PAGE_SIZE;
  const visibleAssets = useMemo(
    () => userAssets.slice(0, visibleLimit),
    [userAssets, visibleLimit]
  );

  const { filters, toggleTraitFilter, filteredAssets, canFilter, hasFilters } =
    useTraitFilters({
      assets: visibleAssets,
    });

  const t = useTranslations('CreateLiquidityPositionForms.SelectInventory');
  const [showFilters, setShowFilters] = useState(false);
  const items = useWatch({ name: 'items', control });

  const totalAssets = userAssets.length || 0;
  const hasMoreAssets = visibleAssets.length < totalAssets;
  const loadMoreAssets = () => {
    setPage((prev) => prev + 1);
  };

  const allSelected = hasFilters
    ? filteredAssets?.length === items?.length
    : items?.length === userAssets.length;

  const selectAll = () => setValue('items', filteredAssets || visibleAssets);
  const removeAll = () => setValue('items', []);

  // if 1155, need more space for counters
  const gridCols = is1155
    ? 'grid-cols-2 @sm:grid-cols-2 @lg:grid-cols-3 @2xl:grid-cols-4 @4xl:grid-cols-6'
    : 'grid-cols-3 @sm:grid-cols-3 @md:grid-cols-4 @xl:grid-cols-5 @2xl:grid-cols-6 @4xl:grid-cols-8 @6xl:grid-cols-10';

  return (
    <section>
      <h1 className="font-medium mb-4 flex justify-between">
        {t('title', { asset: collectionName })}
        {!!items.length && showCounter && (
          <span className="text-gray-500 text-sm ml-4">
            {items.length}
            {' selected'}
          </span>
        )}
      </h1>
      <div className="rounded-lg bg-gray-50 border border-gray-300 px-4 pt-4">
        <header className="flex justify-between items-center border-b border-gray-300 pb-4 mb-4 space-x-4">
          <h3 className="text-sm font-medium text-gray-500 mr-auto">
            {t('items', {
              count: totalAssets,
              visible: visibleAssets.length,
            })}
          </h3>
          <button
            type="button"
            className="text-sm underline text-gray-500 hover:no-underline"
            onClick={allSelected ? removeAll : selectAll}
          >
            {t(allSelected ? 'removeAll' : 'selectAll')}
          </button>
          <FilterToggle
            canFilter={canFilter}
            hasFilters={hasFilters}
            onClick={() => setShowFilters((x) => !x)}
            showFilters={showFilters}
          />
          <FilterModal
            showFilters={showFilters}
            filters={filters}
            onFilter={toggleTraitFilter}
            onClose={() => setShowFilters(false)}
          />
        </header>
        <div
          className={`${
            withinModal
              ? 'max-h-96 hide-scroll overflow-scroll'
              : 'max-h-[50vh] hide-scroll overflow-scroll'
          } pb-4 @container`}
        >
          {status === 'loading' && (
            <div className="p-6 flex items-center justify-center">
              <Icon
                name={Icons.SPINNER_GAP}
                className="animate-spin"
                size={32}
              />
            </div>
          )}
          <section className={`grid ${gridCols} gap-x-1 gap-y-2`}>
            <Controller
              name="items"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InfiniteScroll
                  onLoadMore={loadMoreAssets}
                  scrollable={hasMoreAssets}
                >
                  {filteredAssets?.map((ass) => {
                    return (
                      <SelectInventoryFormItem
                        key={ass.id}
                        {...ass}
                        selectedItems={value}
                        is1155={is1155}
                        onChange={onChange}
                        setValue={setValue}
                      />
                    );
                  })}
                </InfiniteScroll>
              )}
              rules={{
                validate: (items) => {
                  const hasItems = items.some(
                    (item) => item.quantity == null || item.quantity > 0n
                  );
                  if (!hasItems) {
                    return 'Please select some assets before continuing';
                  }
                },
              }}
            />
          </section>
        </div>
      </div>
    </section>
  );
};

export default SelectInventoryForm;
